import { createStore, type StoreApi } from 'zustand';

import {
  RESEARCH_TUTORIAL_SEGMENTS,
  zoneKey,
  type ResearchTutorialPhase,
  type ResearchTutorialSegment,
} from '@/constants/researchTutorial';

/**
 * State for the Research tutorial, scoped to one Ask layout.
 *
 * Pure transitions only. The callout writes, analytics and the tour's own
 * start and stop calls belong to the provider and driver, which read the
 * transitions' results. That keeps every rule about what the tutorial may do
 * next testable without rendering a tour.
 *
 * `phase` is the segment the tutorial is waiting to show, and `running` is the
 * segment actually on screen. They differ while a segment waits for its first
 * target to mount, and when the reader leaves a surface mid-segment: the
 * segment is then interrupted rather than finished, so it starts again when
 * its target comes back.
 *
 * Only `segmentStarted` and `segmentInterrupted` touch `running`. Every other
 * transition moves `phase` alone, and the driver, seeing a running segment the
 * phase has left, stops it on screen and reports the interruption. That is
 * what takes a tour off the screen when a send or a skip moves the tutorial on
 * from outside the tour itself.
 */
export type ZoneEntry = { canAdvance: boolean };

type ResearchTutorialState = {
  phase: ResearchTutorialPhase;
  running: ResearchTutorialSegment | null;
  /** The new conversation the tutorial's question went out in. Its first turn is the one toured. */
  conversationId: string | null;
  /** Mounted targets, keyed by `segment:zone`. */
  zones: Record<string, ZoneEntry>;
  /** Whether the first segment has ever reached the screen in this layout. */
  hasShownFirstSegment: boolean;
};

type ResearchTutorialActions = {
  /** Moves out of `idle` once. Returns whether it did. */
  begin: () => boolean;
  setZone: (key: string, entry: ZoneEntry | null) => void;
  /** Records a segment reaching the screen. Returns whether it is the tutorial's first showing. */
  segmentStarted: (segment: ResearchTutorialSegment) => boolean;
  /** Clears `running` without advancing, so the segment can be shown again. */
  segmentInterrupted: (segment: ResearchTutorialSegment) => void;
  /** Advances past a finished segment. Returns the phase it moved to. */
  finishSegment: (segment: ResearchTutorialSegment) => ResearchTutorialPhase;
  /** The reader sent a first question during the tour. Returns whether the tutorial took it. */
  sendStarted: (conversationId: string) => boolean;
  /** The send for the tutorial's question was refused. Returns to composing it. */
  sendFailed: (conversationId: string) => void;
  skip: () => void;
};

export type ResearchTutorialStore = ResearchTutorialState & ResearchTutorialActions;

const INITIAL_STATE: ResearchTutorialState = {
  phase: 'idle',
  running: null,
  conversationId: null,
  zones: {},
  hasShownFirstSegment: false,
};

export function isTutorialSegment(phase: ResearchTutorialPhase): phase is ResearchTutorialSegment {
  return phase === 'example' || phase === 'compose' || phase === 'results';
}

export function createResearchTutorialStore(): StoreApi<ResearchTutorialStore> {
  return createStore<ResearchTutorialStore>((set, get) => ({
    ...INITIAL_STATE,

    begin: () => {
      if (get().phase !== 'idle') {
        return false;
      }

      set({ phase: 'example' });

      return true;
    },

    setZone: (key, entry) =>
      set((state) => {
        if (entry === null) {
          if (!(key in state.zones)) {
            return state;
          }

          const { [key]: _removed, ...zones } = state.zones;

          return { zones };
        }

        if (state.zones[key]?.canAdvance === entry.canAdvance) {
          return state;
        }

        return { zones: { ...state.zones, [key]: entry } };
      }),

    segmentStarted: (segment) => {
      const isFirstShowing = segment === 'example' && !get().hasShownFirstSegment;

      set({ running: segment, hasShownFirstSegment: get().hasShownFirstSegment || isFirstShowing });

      return isFirstShowing;
    },

    segmentInterrupted: (segment) => {
      if (get().running === segment) {
        set({ running: null });
      }
    },

    finishSegment: (segment) => {
      const { phase } = get();

      if (phase !== segment) {
        return phase;
      }

      const next = RESEARCH_TUTORIAL_SEGMENTS[segment].next;

      set({ phase: next });

      return next;
    },

    sendStarted: (conversationId) => {
      const { phase } = get();

      if (phase !== 'example' && phase !== 'compose') {
        return false;
      }

      set({ phase: 'results', conversationId });

      return true;
    },

    sendFailed: (conversationId) => {
      const { phase, conversationId: sentIn } = get();

      if (phase !== 'results' || sentIn !== conversationId) {
        return;
      }

      set({ phase: 'compose', conversationId: null });
    },

    skip: () => set({ phase: 'done' }),
  }));
}

export function selectIsTutorialLive(state: ResearchTutorialStore): boolean {
  return isTutorialSegment(state.phase);
}

export function selectZoneCanAdvance(
  segment: ResearchTutorialSegment,
  zone: number
): (state: ResearchTutorialStore) => boolean {
  return (state) => state.zones[zoneKey(segment, zone)]?.canAdvance ?? true;
}

/** The segment waiting to show, once its first target has mounted; `null` otherwise. */
export function selectReadySegment(state: ResearchTutorialStore): ResearchTutorialSegment | null {
  const { phase, zones } = state;

  return isTutorialSegment(phase) && zoneKey(phase, 1) in zones ? phase : null;
}
